import { RangeContainer, SliderContainer } from '@/styles/fields';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface RangeFieldProps extends INonNullableField<[number, number]> {
	setValueOnRelease?: boolean;
	onRelease?: (value: [number, number], from: 'min' | 'max') => void;
	blockMinAt?: number;
	blockMaxAt?: number;
	min: number;
	max: number;
}

const RangeField: React.FC<RangeFieldProps> = ({
	min,
	blockMinAt,
	max,
	blockMaxAt,
	value,
	setValueOnRelease,
	setValue,
	onRelease,
}) => {
	const [minVal, setMinVal] = useState(value![0]);
	const [maxVal, setMaxVal] = useState(value![1]);
	const rangeRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setMinVal(value![0]);
		setMaxVal(value![1]);
	}, [value]);

	const getPercent = useCallback(
		(v: number) => Math.round(((v - min) / (max - min)) * 100),
		[minVal, maxVal]
	);

	useEffect(() => {
		const minPercent = getPercent(minVal);
		const maxPercent = getPercent(maxVal);

		if (rangeRef.current) {
			rangeRef.current.style.left = `${minPercent}%`;
			rangeRef.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, [minVal, getPercent]);

	useEffect(() => {
		const minPercent = getPercent(minVal);
		const maxPercent = getPercent(maxVal);

		if (rangeRef.current) {
			rangeRef.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, [maxVal, getPercent]);

	useEffect(() => {
		!setValueOnRelease && setValue?.([minVal, maxVal]);
	}, [minVal, maxVal, setValue]);

	const areValuesEqual =
		JSON.stringify(value) === JSON.stringify([minVal, maxVal]);

	return (
		<RangeContainer>
			<input
				type='range'
				className='thumb thumb--left'
				min={min}
				max={max}
				value={minVal}
				onChange={(e) => {
					const value = Math.min(
						e.target.valueAsNumber,
						blockMinAt ?? maxVal - 1
					);
					setMinVal(value);
				}}
				onMouseUp={() => {
					!areValuesEqual && onRelease?.([minVal, maxVal], 'min');
				}}
				style={{ zIndex: minVal > max - 100 ? '5' : undefined }}
			/>
			<input
				type='range'
				className='thumb thumb--right'
				min={min}
				max={max}
				value={maxVal}
				onChange={(e) => {
					const value = Math.max(
						e.target.valueAsNumber,
						blockMaxAt ?? minVal + 1
					);
					setMaxVal(value);
				}}
				onMouseUp={() => {
					!areValuesEqual && onRelease?.([minVal, maxVal], 'max');
				}}
			/>

			<SliderContainer>
				<div className='slider__track' />
				<div className='slider__range' ref={rangeRef} />
				<div className='slider__left-value'>{minVal}</div>
				<div className='slider__right-value'>{maxVal}</div>
			</SliderContainer>
		</RangeContainer>
	);
};

export default RangeField;
